import { Component, EventEmitter, Inject, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MenuService } from '../menu.service';
import { CacheService } from '../../../common/services/cache.service';
import { CACHE_KEYS } from '../../../common/constants/cache-keys';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { COMPONENT_META_DATA } from '../../../common/constants/component-meta-data';
import { Bookmarks, ChildMenuObj, ParentMenuObj, SubChildMenuObj } from '../menu.model';
import { LoginResults } from '../../../login/login.viewmodel';
import { ButtonComponent } from '../../../common/components/general/button/button.component';
import { IconComponent } from '../../../common/components/general/icon/icon.component';
import { NgClass, NgIf } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ContentFilterPipe } from '../../../common/pipes/content-filter.pipe';
import { AutoSearchComponent } from '../../../common/components/auto-search/auto-search.component';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { LPMI_ROUTES } from '../../../common/constants/routes';
import { DialogMenuData } from './dialog-menu.model';
import { ElementsService } from '../../../common/services/elements.service';
import { getUrlByCategory } from '../../../common/util/utils';
import { GtmEvent } from '../../../common/models/gtm-event.model';
import { GtmService } from '../../../common/services/gtm/gtm.service';

@Component({
  selector: 'nuc-dialog-menu',
  imports: [MatListItem, MatNavList, MatSidenavModule, ButtonComponent, IconComponent, NgIf, NgClass, ContentFilterPipe, AutoSearchComponent, ButtonComponent, MatAccordion, MatExpansionModule],
  templateUrl: './dialog-menu.component.html',
  styleUrl: './dialog-menu.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DialogMenuComponent {
  @Output() dialogClosed = new EventEmitter<void>();

  parentMenu: ParentMenuObj[];
  bookmarked: Bookmarks[];
  childMenu: ChildMenuObj[];
  selectedSection: string;
  subChildMenu: SubChildMenuObj[];
  tier3GroupMenu: SubChildMenuObj[] = [];
  frequentlyVisited: SubChildMenuObj[] = [];
  componentType: string = COMPONENT_META_DATA.HEADER.VALUE;
  selectedTier1Index: number = 0;
  selectedTier2Index: number;
  selectedTier3Index: number | null = null;
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  userType: string;
  loginData: LoginResults;

  searchCtrl = new FormControl('');
  searchSubChildItems: ChildMenuObj[] = [];
  filterKeys: string[];
  bookmarkedNames: string[];
  bookmarkDialogRef: MatDialogRef<any>;
  @ViewChild('bookmarkDialogTemplate') dialogTemplate: TemplateRef<any>;
  widgetCustomisation: string = LPMI_ROUTES.WIDGET_CUSTOMIZATION.PATH;
  // Track the expanded state of parent and child panels
  parentExpanded: boolean[] = [];
  childExpanded: boolean[][] = [];
  constructor(
    private overlayContainer: OverlayContainer,
    public bookmarkDialog: MatDialog,
    public dialogRef: MatDialogRef<DialogMenuComponent>,
    private menuService: MenuService,
    @Inject(MAT_DIALOG_DATA) public data: DialogMenuData,
    private elementsService: ElementsService,
    private router: Router,
    private gtm: GtmService,
    private cacheService: CacheService
  ) { }



  // Method to handle the toggling of parent accordion
  onParentPanelToggle(parentIndex: number) {
    // Close all child panels when parent panel is expanded
    this.childExpanded[parentIndex] = this.childExpanded[parentIndex] || [];
    this.childExpanded[parentIndex].fill(false); // collapse all children of this parent
    const parent = this.parentMenu?.[parentIndex];
    if (parent?.category?.toLowerCase() === 'faq') {
      this.navigateToLink(parent.link, parent.linkType, parent.category, null, parent.label, (parent as any).subCategory);
    }
  }

  // Method to handle the toggling of child accordion
  onChildPanelToggle(parentIndex: number, childIndex: number) {
    if (!this.childExpanded[parentIndex]) {
      this.childExpanded[parentIndex] = [];
    }

    const child = this.parentMenu?.[parentIndex]?.childMenu?.[childIndex];
    if (child?.category?.toLowerCase() === 'faq') {
      this.childExpanded[parentIndex][childIndex] = false;
      return;
    }

    // Close the other child panels in the same parent accordion
    this.childExpanded[parentIndex][childIndex] = !this.childExpanded[parentIndex][childIndex];

    // Close all other child panels for the parent except the one toggled
    for (let i = 0; i < this.parentMenu[parentIndex].childMenu.length; i++) {
      if (i !== childIndex) {
        this.childExpanded[parentIndex][i] = false;
      }
    }
  }

  ngOnInit() {
    this.selectedSection = 'frequentlyUsed';
    this.filterKeys = ['label', 'linkDescription',' parentMenuLabel','childMenuLabel'];
    
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    console.log('User Type in Dialog Menu:', this.userType);
    this.initializePage();
  }

  private initializePage() {
    this.parentMenu = this.data?.appLinks?.parentMenu;
    this.parentMenu?.forEach((item: any) => {
      this.applyEmbeddedFaq(item);
      item.hasChild = item?.childMenu?.length ? true : false;
      item?.childMenu?.forEach((childItem: ChildMenuObj) => this.applyEmbeddedFaq(childItem));
    });
    this.bookmarked = this.data?.appLinks?.bookmarked ?? [];
    this.bookmarkedNames = this.bookmarked.map((item: any) => item.name);
    this.parentMenu?.forEach((parentItem: ParentMenuObj) => {
      parentItem?.childMenu?.forEach((childItem: ChildMenuObj) => {
        const normalizedSubChildMenu = this.getNormalizedSubChildMenu(childItem);
        childItem.subChildMenu = normalizedSubChildMenu;
        normalizedSubChildMenu.forEach((subChildItem: SubChildMenuObj) => {
          if (subChildItem) {
            subChildItem.parentMenuLabel = parentItem?.label;
            subChildItem.childMenuLabel = childItem?.label;
            this.getActionItems(subChildItem).forEach((menuItem: Bookmarks | SubChildMenuObj) => {
              this.decorateMenuItem(menuItem, parentItem?.label, childItem?.label);
            });
          }
        });

        childItem.hasChild = normalizedSubChildMenu.some((subMenuItem: SubChildMenuObj) => this.getActionItems(subMenuItem).length > 0);
      });
    });
  }

  private applyEmbeddedFaq(menuItem: ParentMenuObj | ChildMenuObj) {
    const faq = (menuItem as any)?.components
      ?.flatMap((component: any) => component?.data ?? [])
      .find((item: any) => item?.category?.toLowerCase() === 'faq');

    if (faq) {
      menuItem.label = menuItem.label || faq.label;
      menuItem.link = menuItem.link || faq.link;
      menuItem.linkType = menuItem.linkType || faq.linkType;
      menuItem.category = menuItem.category || faq.category;
      menuItem.isFrequentlyVisited = menuItem.isFrequentlyVisited ?? faq.isFrequentlyVisited;
      (menuItem as any).subCategory = (menuItem as any).subCategory || faq.subCategory;
    }
  }

  getNormalizedSubChildMenu(childItem: ChildMenuObj): SubChildMenuObj[] {
    return childItem?.subChildMenu ?? (childItem as any)?.subchildMenu ?? [];
  }

  private getSubChildMenuItems(subChildItem: SubChildMenuObj): Bookmarks[] {
    return (subChildItem as any)?.components?.flatMap((component: any) => component?.data ?? []) ?? [];
  }

  getActionItems(subChildItem: SubChildMenuObj): (Bookmarks | SubChildMenuObj)[] {
    const flattenedItems = this.getSubChildMenuItems(subChildItem);
    return flattenedItems.length > 0 ? flattenedItems : (subChildItem?.link ? [subChildItem] : []);
  }

  private decorateMenuItem(menuItem: Bookmarks | SubChildMenuObj, parentMenuLabel?: string, childMenuLabel?: string) {
    menuItem.parentMenuLabel = parentMenuLabel;
    menuItem.childMenuLabel = childMenuLabel;
    menuItem.isBookmarked = this.bookmarkedNames.includes(menuItem?.name) ? true : false;
    menuItem.iconClass = menuItem?.isBookmarked ? 'bookmark' : 'bookmark_outlined';
    this.searchSubChildItems.push(menuItem as SubChildMenuObj);
    if (menuItem?.isFrequentlyVisited) {
      this.frequentlyVisited.push(menuItem as SubChildMenuObj);
    }

    this.bookmarked?.forEach((bookmarkedItem: Bookmarks) => {
      if (bookmarkedItem.name === menuItem.name) {
        bookmarkedItem.parentMenuLabel = parentMenuLabel;
        bookmarkedItem.childMenuLabel = childMenuLabel;
      }
    });
  }

  navigateToSection(dataItem: string | ParentMenuObj, index?: number) {
    this.selectedTier1Index = index;
    if (typeof dataItem === 'string') {
      this.selectedSection = dataItem;
      this.navigateToSubsection(null, 'level1');
    } else {
      if (dataItem?.category?.toLowerCase() === 'faq') {
        this.navigateToLink(dataItem.link, dataItem.linkType, dataItem.category, null, dataItem.label, (dataItem as any).subCategory);
        return;
      }
      dataItem?.childMenu?.forEach((item: any) => {
        item.hasChild = this.getNormalizedSubChildMenu(item).some((subMenuItem: SubChildMenuObj) => this.getActionItems(subMenuItem).length > 0);
      });
      this.childMenu = dataItem.childMenu;
      this.selectedSection = dataItem.name;
      this.navigateToSubsection(null, 'level1');
    }
  }

  navigateToSubsection(dataItemChild: ChildMenuObj, level?: string, index?: number) {
    this.selectedTier2Index = index;
    this.selectedTier3Index = null;
    this.tier3GroupMenu = [];
    this.subChildMenu = [];
    if (dataItemChild?.category?.toLowerCase() === 'faq') {
      this.navigateToLink(dataItemChild.link, dataItemChild.linkType, dataItemChild.category, null, dataItemChild.label, (dataItemChild as any).subCategory);
      return;
    }
    if (level) {
      this.subChildMenu = dataItemChild?.subChildMenu;
    } else {
      if (dataItemChild?.hasChild) {
        this.tier3GroupMenu = this.getNormalizedSubChildMenu(dataItemChild);
        this.tier3GroupMenu.forEach((groupItem: SubChildMenuObj) => {
          this.getActionItems(groupItem).forEach((menuItem: Bookmarks | SubChildMenuObj) => {
            menuItem.iconClass = menuItem?.isBookmarked ? 'bookmark' : 'bookmark_outlined';
          });
        });
      }
    }
  }

  handleTier3Click(item: SubChildMenuObj, index: number) {
    this.selectedTier3Index = index;
    const actionItems = this.getActionItems(item);
    if (!item?.link && actionItems.length > 0) {
      this.subChildMenu = actionItems as SubChildMenuObj[];
      this.subChildMenu.forEach((menuItem: SubChildMenuObj) => {
        menuItem.iconClass = menuItem?.isBookmarked ? 'bookmark' : 'bookmark_outlined';
      });
      return;
    }

    this.handleClick(item);
  }

  navigateToMobileSubchild(childItem: ChildMenuObj[], parentIndex: number, childIndex: number) {
    this.selectedTier3Index = null;
    this.subChildMenu = [];
    const selectedChild = this.parentMenu?.[parentIndex]?.childMenu?.[childIndex];
    if (selectedChild?.category?.toLowerCase() === 'faq') {
      this.navigateToSubsection(selectedChild, '', childIndex);
      return;
    }
    this.parentMenu[parentIndex].childMenu?.forEach((item: ChildMenuObj) => {
      this.getNormalizedSubChildMenu(item).forEach((subChildItem: SubChildMenuObj) => {
        this.getActionItems(subChildItem).forEach((menuItem: Bookmarks | SubChildMenuObj) => {
          menuItem.iconClass = menuItem?.isBookmarked ? 'bookmark' : 'bookmark_outlined';
        });
      });
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
    // this.dialogClosed.emit();
  }

  closeBookmarkDialog() {
    this.bookmarkDialogRef.close();
  }

  private getPagePathContext(link: string, category?: string) {
    if (!link || !category) {
      return undefined;
    }

    const normalizedLink = link.startsWith('/') ? link.slice(1) : link;
    const [routePath, appName] = normalizedLink.split('/');

    if (!routePath) {
      return undefined;
    }

    return {
      routePath,
      appName,
      pagePath: category,
    };
  }

  navigateToLink(link?: string, linkType?: string, category?: string, event?: Event, linkLabel?: string, appName?: string) {

    const gtmEvent = GtmEvent.create("Header", 'Header', 'Redirection::External', linkLabel);
    this.gtm.pushEvent(gtmEvent);

    if (event) {
      event.stopPropagation(); // Prevents the click event from bubbling up
    }

    if (category != "external") {
      link = getUrlByCategory(category, appName);
      const pagePathContext = this.getPagePathContext(link, category);
      if (pagePathContext) {
        this.cacheService.addToCache(CACHE_KEYS.NAVIGATION.PAGE_PATH_CONTEXT, pagePathContext);
      }
    }

    const isSameDomain = this.isWithinSameDomain(link);
    if (link) {
      this.closeDialog();
      if (linkType == 'default' && isSameDomain.sameDomain) {
        link = isSameDomain.relativeLink;
        this.router.navigate([link]);
      } else {
        this.openWindow(link, linkType);
      }
    }
  }

  private openWindow(link: string, linkType: string) {
    if (linkType === 'default') {
      // For external links, use window.location.href
      // window.location.href = link;
      window.open(link, '_self');
    } else {
      // For internal links, use Angular Router
      window.open(link, '_blank');
    }
  }

  onOptionSelected(selectedOption: SubChildMenuObj) {
    if (selectedOption) {
      this.closeDialog();
      this.navigateToLink(selectedOption.link, selectedOption.linkType, selectedOption.category, null, '', selectedOption.subCategory);
    }
  }

  handleClick(item: any) {
    if (item.linkType !== 'newTab') {
      this.navigateToLink(item.link, item.linkType, item.category, null, '', item.subCategory);
    }
  }

  updateBookmark(item: Bookmarks) {
    if (this.bookmarkedNames.includes(item.name)) {
      // If the item is already bookmarked, remove it
      this.menuService.updateBookmark(item, 'remove', '').subscribe((response) => {
        if (response.results) {
          this.bookmarked = this.bookmarked.filter((bookmarkedItem: Bookmarks) => bookmarkedItem.name !== item.name);
          this.bookmarkedNames = this.bookmarked.map((item: Bookmarks) => item.name);
          item.isBookmarked = false;
          item.iconClass = 'bookmark_outlined';
        }
      });
    } else {
      // If the item is not bookmarked, add it
      this.menuService.updateBookmark(item, 'add', '').subscribe((response) => {
        if (response.results) {
          this.bookmarked.push(item);
          this.bookmarkedNames = this.bookmarked.map((item: Bookmarks) => item.name);
          item.isBookmarked = true;
          item.iconClass = 'bookmark';
        } else {
          if (response.errorData.httpStatus === '400') {
            this.displayErrorPopup(response);
          }
        }
      });
    }
  }

  displayErrorPopup(response: any) {
    this.bookmarkDialogRef = this.bookmarkDialog.open(this.dialogTemplate, {
      width: '560px',
      minHeight: '218px',
      panelClass: 'bookmark-dialog',
      data: response.errorData.errorList[0].errorMsg,
    });
    this.overlayContainer.getContainerElement().classList.add('bookmark-overlay');
    this.bookmarkDialogRef.afterClosed().subscribe(() => {
      this.overlayContainer.getContainerElement().classList.remove('bookmark-overlay');
    });
  }

  removeBookmark(item: Bookmarks) {
    this.menuService.updateBookmark(item, 'remove', '').subscribe((response) => {
      if (response) {
        this.bookmarked = this.bookmarked.filter((i: Bookmarks) => i !== item);
        this.bookmarkedNames = this.bookmarked.map((item: Bookmarks) => item.name);
        this.parentMenu?.forEach((parentItem: ParentMenuObj) => {
          parentItem?.childMenu?.forEach((childItem: ChildMenuObj) => {
            this.getNormalizedSubChildMenu(childItem)?.forEach((subChildItem: SubChildMenuObj) => {
              this.getActionItems(subChildItem).forEach((menuItem: Bookmarks | SubChildMenuObj) => {
                menuItem.isBookmarked = this.bookmarkedNames.includes(menuItem?.name) ? true : false;
                menuItem.iconClass = menuItem?.isBookmarked ? 'bookmark' : 'bookmark_outlined';
              });
            });
          });
        });
      }
    });
  }

  private isWithinSameDomain(link: string): { sameDomain: boolean; relativeLink: string } {
    const sameAppUrl = window.location.origin;
    let sameDomain = false;
    if (link?.startsWith('/') || link?.startsWith(sameAppUrl)) {
      sameDomain = true;
      link = link.replace(sameAppUrl, ''); // Convert full URL to relative path for Angular routin
    }
    return {
      sameDomain,
      relativeLink: link,
    };
  }

}
